import { useState } from "react";
import { X, Trash2, HardHat, MapPin, Award, Phone, Mail, CalendarDays, UserPlus, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

export function EmployeesTab({ employees, onRefresh }) {
  const [modal, setModal] = useState(null);

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8" data-testid="employees-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Employees</div>
          <p className="mt-1 text-sm text-[#23282B]/60">Crew records — demographics, start date, current job site, certifications & licenses.</p>
        </div>
        <button onClick={() => setModal({ initial: null })} data-testid="employee-add" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
          <UserPlus size={15} /> Add Employee
        </button>
      </div>

      {employees.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-[#23282B]/20 p-10 text-center text-[#23282B]/60" data-testid="employees-empty">
          <HardHat size={28} className="mx-auto text-[#2F5F80]" />
          <div className="mt-2 text-sm">No employee records yet — add your crew.</div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="employees-list">
          {employees.map((emp) => (
            <article key={emp.id} className={`border-2 p-5 transition-colors ${emp.status === "inactive" ? "border-[#23282B]/10 opacity-60" : "border-[#23282B]/10 hover:border-[#23282B]/25"}`} data-testid={`employee-${emp.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 shrink-0 inline-flex items-center justify-center bg-[#23282B] text-[#9CC4DB]"><HardHat size={18} /></span>
                  <div>
                    <button onClick={() => setModal({ initial: emp })} data-testid={`employee-edit-${emp.id}`} className="font-display text-xl uppercase text-[#23282B] hover:text-[#2F5F80] transition-colors text-left">{emp.name}</button>
                    <div className="text-xs text-[#2F5F80] font-bold uppercase tracking-wider">{emp.position || "Crew"}</div>
                  </div>
                </div>
                {emp.status === "inactive" && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#23282B]/10 text-[#23282B]/60 px-2 py-0.5">Inactive</span>}
              </div>

              <div className="mt-3 space-y-1.5 text-xs text-[#23282B]/75">
                {emp.phone && <div className="flex items-center gap-2"><Phone size={12} className="text-[#2F5F80]" /> {emp.phone}</div>}
                {emp.email && <div className="flex items-center gap-2"><Mail size={12} className="text-[#2F5F80]" /> {emp.email}</div>}
                {emp.start_date && <div className="flex items-center gap-2"><CalendarDays size={12} className="text-[#2F5F80]" /> Started {emp.start_date}</div>}
                {emp.current_job_site && <div className="flex items-center gap-2"><MapPin size={12} className="text-[#2F5F80]" /> {emp.current_job_site}</div>}
                {emp.emergency_contact_name && <div className="flex items-center gap-2"><ShieldAlert size={12} className="text-[#2F5F80]" /> ICE: {emp.emergency_contact_name}{emp.emergency_contact_phone ? ` — ${emp.emergency_contact_phone}` : ""}</div>}
              </div>

              {emp.certifications?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`employee-certs-${emp.id}`}>
                  {emp.certifications.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#9CC4DB]/15 text-[#2F5F80] px-2 py-1"><Award size={10} /> {c}</span>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {modal && <EmployeeForm initial={modal.initial} onClose={() => setModal(null)} onSaved={onRefresh} />}
    </section>
  );
}

function EmployeeForm({ initial, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState({
    name: initial?.name || "", position: initial?.position || "",
    email: initial?.email || "", phone: initial?.phone || "",
    address: initial?.address || "", city: initial?.city || "",
    dob: initial?.dob || "", start_date: initial?.start_date || "",
    current_job_site: initial?.current_job_site || "",
    certifications: (initial?.certifications || []).join(", "),
    emergency_contact_name: initial?.emergency_contact_name || "",
    emergency_contact_phone: initial?.emergency_contact_phone || "",
    notes: initial?.notes || "",
    status: initial?.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { ...form, certifications: form.certifications.split(",").map((c) => c.trim()).filter(Boolean) };
    if (!editing) delete payload.status;
    try {
      if (editing) await api.patch(`/employees/${initial.id}`, payload);
      else await api.post("/employees", payload);
      await onSaved();
      onClose();
    } catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this employee record?")) return;
    setBusy(true);
    try { await api.delete(`/employees/${initial.id}`); await onSaved(); onClose(); }
    catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="employee-form-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-2xl p-7 border-t-4 border-[#9CC4DB] my-auto max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">{editing ? "Edit Employee" : "Add Employee"}</div>
          <button type="button" onClick={onClose} data-testid="employee-form-close" className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Full Name *</span>
            <input data-testid="employee-name" required value={form.name} onChange={set("name")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Position</span>
            <input data-testid="employee-position" value={form.position} onChange={set("position")} className={inputCls} placeholder="e.g. Operator, Foreman" /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Email</span>
            <input data-testid="employee-email" value={form.email} onChange={set("email")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Phone</span>
            <input data-testid="employee-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Address</span>
            <input data-testid="employee-address" value={form.address} onChange={set("address")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">City</span>
            <input data-testid="employee-city" value={form.city} onChange={set("city")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Date of Birth</span>
            <input data-testid="employee-dob" type="date" value={form.dob} onChange={set("dob")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Start Date</span>
            <input data-testid="employee-start-date" type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} /></label>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Current Job Site</span>
            <input data-testid="employee-job-site" value={form.current_job_site} onChange={set("current_job_site")} className={inputCls} placeholder="e.g. Maple Bay Foundation — Duncan" /></label>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Certifications / Licenses (comma-separated)</span>
            <input data-testid="employee-certifications" value={form.certifications} onChange={set("certifications")} className={inputCls} placeholder="e.g. Class 3 w/ Air, WHMIS, First Aid Level 1, Excavator Ticket" /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Emergency Contact Name</span>
            <input data-testid="employee-ice-name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Emergency Contact Phone</span>
            <input data-testid="employee-ice-phone" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} className={inputCls} /></label>
          {editing && (
            <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Status</span>
              <select data-testid="employee-status" value={form.status} onChange={set("status")} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select></label>
          )}
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Notes</span>
            <textarea data-testid="employee-notes" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} /></label>
        </div>
        {error && <div data-testid="employee-form-error" className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="mt-6 flex items-center gap-3">
          <button type="submit" disabled={busy} data-testid="employee-form-submit" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-50">
            {busy ? "Saving…" : editing ? "Save Changes" : "Add Employee"}
          </button>
          {editing && (
            <button type="button" onClick={remove} disabled={busy} data-testid="employee-delete" className="ml-auto inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-xs font-bold tracking-[0.12em] uppercase">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
